/**
 * Phone / tablet shell (docs/MOBILE.md). Installed once by the touch-controls system when there is a DOM:
 *  - decides whether the touch UI is on (`touchUi`, mirrored as `html.bh-touch` for CSS) — at boot from
 *    the pointer type, or later on the first real finger on the screen (hybrid laptops);
 *  - stops the browser's own touch behaviour fighting the game: pinch / double-tap zoom, page scroll,
 *    pull-to-refresh, long-press menus and text selection (scrollable panels and text fields excepted);
 *  - asks for fullscreen + a landscape orientation lock on the first tap, where the browser allows it;
 *  - shows a "turn it sideways" card in portrait (pure CSS, so it also covers the title screen);
 *  - knows when it runs as a home-screen web app (`html.bh-standalone`), and on iPhone Safari, where
 *    there is no fullscreen API, shows a one-time "Add to Home Screen" hint instead.
 */
import { prefersTouchUi, touchUi } from '../input/touch';
import { PORTRAIT_ART_URL } from './art';
import { el } from './dom';

/** Elements that keep native touch scrolling / editing. */
const NATIVE_TOUCH = '.ol-body, .sc-scroll, .bh-online-card, .bh-controls .cols, [data-touch-scroll], input, textarea, select';

export interface MobileShell {
  /** The portrait warning element (inside the UI root). */
  readonly rotateCard: HTMLElement;
  /** The iPhone Safari "Add to Home Screen" hint, while it is up. */
  readonly hint: HTMLElement | null;
  dispose(): void;
}

export function installMobileShell(uiRoot: HTMLElement): MobileShell {
  const doc = document;
  const html = doc.documentElement;
  const offs: (() => void)[] = [];
  const on = <K extends keyof DocumentEventMap>(type: K, fn: (e: DocumentEventMap[K]) => void, opts: AddEventListenerOptions = {}): void => {
    doc.addEventListener(type, fn as EventListener, opts);
    offs.push(() => doc.removeEventListener(type, fn as EventListener, opts));
  };

  const sync = (enabled: boolean): void => {
    html.classList.toggle('bh-touch', enabled);
  };
  offs.push(touchUi.onChange(sync));
  touchUi.set(prefersTouchUi());
  sync(touchUi.enabled);

  // A real finger on a device that booted in desktop mode (touch-screen laptop): switch the touch UI on.
  on(
    'pointerdown',
    (e) => {
      if (e.pointerType === 'touch' && !touchUi.enabled && !new URLSearchParams(location.search).has('notouch')) touchUi.set(true);
    },
    { capture: true },
  );

  // ---- taps made while another finger is down. Browsers send no click for those, so a menu would
  // ignore the player while their other thumb still rests on the stick or GAS. If no real click
  // follows a short, still tap of a second finger, click the element it landed on. The touch layer
  // reads pointers itself and text fields keep their native behaviour, so both are left out.
  const touchesDown = new Set<number>();
  const secondTaps = new Map<number, { target: Element; x: number; y: number }>();
  let lastClick: { target: EventTarget | null; at: number } = { target: null, at: -Infinity };
  on(
    'pointerdown',
    (e) => {
      if (e.pointerType !== 'touch') return;
      const t = e.target;
      if (touchUi.enabled && touchesDown.size > 0 && t instanceof Element && !t.closest('.bh-tc-layer, input, textarea, select')) {
        secondTaps.set(e.pointerId, { target: t, x: e.clientX, y: e.clientY });
      }
      touchesDown.add(e.pointerId);
    },
    { capture: true },
  );
  on(
    'pointerup',
    (e) => {
      touchesDown.delete(e.pointerId);
      const tap = secondTaps.get(e.pointerId);
      secondTaps.delete(e.pointerId);
      if (!tap || Math.hypot(e.clientX - tap.x, e.clientY - tap.y) > 12) return;
      const upAt = performance.now();
      setTimeout(() => {
        const clicked = lastClick.at >= upAt && lastClick.target instanceof Node && tap.target.contains(lastClick.target);
        if (!clicked && tap.target.isConnected && tap.target instanceof HTMLElement) tap.target.click();
      }, 90);
    },
    { capture: true },
  );
  on(
    'pointercancel',
    (e) => {
      touchesDown.delete(e.pointerId);
      secondTaps.delete(e.pointerId);
    },
    { capture: true },
  );
  on('click', (e) => (lastClick = { target: e.target, at: performance.now() }), { capture: true });

  // ---- browser gestures. Only while the touch UI is on, so desktop behaviour is unchanged.
  const native = (t: EventTarget | null): boolean => t instanceof Element && !!t.closest(NATIVE_TOUCH);
  on(
    'touchmove',
    (e) => {
      if (!touchUi.enabled) return;
      // Pinch (2+ fingers) is never ours to give away; a single finger may scroll a panel.
      if (e.touches.length > 1 || !native(e.target)) e.preventDefault();
    },
    { passive: false },
  );
  // No preventDefault on a second finger's touchstart: that would cancel its click, and menus must
  // answer a tap while the other thumb still rests on the stick or GAS. touch-action: none (touch.css),
  // the touchmove guard above and the gesture events below already stop the pinch.
  // iOS Safari pinch events (non-standard).
  for (const type of ['gesturestart', 'gesturechange'] as const) {
    const fn = (e: Event): void => {
      if (touchUi.enabled) e.preventDefault();
    };
    doc.addEventListener(type, fn, { passive: false });
    offs.push(() => doc.removeEventListener(type, fn));
  }
  on('dblclick', (e) => {
    if (touchUi.enabled) e.preventDefault();
  });
  on('contextmenu', (e) => {
    if (touchUi.enabled && !native(e.target)) e.preventDefault();
  });
  on('selectstart', (e) => {
    if (touchUi.enabled && !native(e.target)) e.preventDefault();
  });

  // iOS ignores user-scalable=no and zooms on a quick second tap unless that tap's touchend is cancelled.
  // touch-action: none already covers most of the page; this catches the rest. Cancelling also drops
  // the tap's click, so a menu entry tapped twice in a row is clicked by hand.
  let lastTap = { at: -Infinity, x: 0, y: 0 };
  on(
    'touchend',
    (e) => {
      if (!touchUi.enabled || e.touches.length > 0 || e.changedTouches.length !== 1) return;
      const t = e.changedTouches[0]!;
      const now = performance.now();
      const quick = now - lastTap.at < 350 && Math.hypot(t.clientX - lastTap.x, t.clientY - lastTap.y) < 40;
      lastTap = quick ? { at: -Infinity, x: 0, y: 0 } : { at: now, x: t.clientX, y: t.clientY };
      if (!quick || !e.cancelable || native(e.target)) return;
      const target = e.target;
      if (target instanceof Element && target.closest('.bh-tc-layer')) return; // the controls cancel their own
      e.preventDefault();
      // Any element, SVG icons inside buttons included (they have no .click()); it bubbles to the button.
      if (target instanceof Element && target.isConnected) {
        target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX: t.clientX, clientY: t.clientY }));
      }
    },
    { passive: false },
  );

  // ---- home-screen web app (iOS "Add to Home Screen", an installed Android app): no browser bars, so
  // nothing to ask for. Mirrored as html.bh-standalone for CSS.
  const standalone = isStandalone();
  html.classList.toggle('bh-standalone', standalone);
  html.classList.toggle('bh-ios', isIOS());

  // ---- fullscreen + landscape lock on the first tap (needs a user activation; pointerup counts for touch).
  let asked = standalone;
  const goFullscreen = (e: Event): void => {
    if (asked || !touchUi.enabled) return;
    if (e instanceof PointerEvent && e.pointerType === 'mouse') return;
    // Automated browsers (Playwright) keep their fixed viewport.
    if (navigator.webdriver) return;
    asked = true;
    const req = html.requestFullscreen?.bind(html);
    if (!req || doc.fullscreenElement) return lockLandscape();
    req({ navigationUI: 'hide' })
      .then(lockLandscape)
      .catch(() => undefined);
  };
  on('pointerup', goFullscreen, { capture: true });
  on('click', goFullscreen, { capture: true });

  // ---- portrait warning (shown by CSS: html.bh-touch + orientation: portrait)
  const rotateCard = el('div', 'bh-ui bh-rotate', undefined, uiRoot);
  // the key art's portrait crop behind the card (already cached: the portrait loading screen showed it)
  rotateCard.style.setProperty('--bh-rotate-art', `url("${PORTRAIT_ART_URL}")`);
  rotateCard.innerHTML =
    '<div class="card bh-glass"><svg viewBox="0 0 64 64" aria-hidden="true"><rect class="phone" x="22" y="8" width="20" height="36" rx="3"/><path class="arrow" d="M14 50 a20 20 0 0 0 30 6 m0 0 l-1 -7 m1 7 l-7 1"/></svg>' +
    '<b>Turn it sideways</b><span>Borough Heat plays in landscape.</span>' +
    `<small class="a2hs-tip">Full screen: tap ${SHARE_ICON} Share, then <i>Add to Home Screen</i>.</small></div>`;

  // ---- iPhone Safari: no fullscreen API, so the way to lose the browser bars is the home screen.
  // A one-time, dismissible hint (remembered once closed).
  let hint: HTMLElement | null = null;
  let hintWait: ReturnType<typeof setTimeout> | 0 = 0;
  offs.push(() => hintWait && clearTimeout(hintWait));
  const showHint = (): void => {
    if (hint || standalone || !isIOS() || !touchUi.enabled || readFlag(HINT_KEY) || new URLSearchParams(location.search).has('noa2hs')) return;
    // A calm moment only (src/ui/attention.ts HUD_RULES.hint; uiSystem puts html.bh-calm up then: on foot,
    // no heat, no menu): it used to come up
    // 1.2 s after loading whatever was going on, and in a chase it lay over the police on the radio.
    if (!html.classList.contains('bh-calm')) {
      if (!hintWait) hintWait = setTimeout(() => ((hintWait = 0), showHint()), 1000);
      return;
    }
    hint = el('div', 'bh-ui bh-a2hs', undefined, uiRoot);
    html.classList.add('bh-a2hs-open');
    hint.innerHTML =
      '<div class="card bh-glass" role="note"><div class="txt"><b>Play full screen</b>' +
      `<span>Tap ${SHARE_ICON} <i>Share</i>, then <i>Add to Home Screen</i>. Start Borough Heat from its icon: no Safari bars.</span></div>` +
      '<button type="button" class="ok">Got it</button></div>';
    const close = (e: Event): void => {
      e.preventDefault();
      writeFlag(HINT_KEY);
      html.classList.remove('bh-a2hs-open');
      const gone = hint;
      hint = null;
      if (!gone) return;
      // Fade out, but keep catching the click that trails the finger so it can't land on the game below.
      gone.classList.add('is-gone');
      setTimeout(() => gone.remove(), 400);
    };
    const ok = hint.querySelector('.ok') as HTMLElement;
    ok.addEventListener('click', close);
    // Between the thumbs where it fits; at a big touch size on a small phone that band is ~100 px and the card
    // became a column of one word a line over the HUD, so it goes above the controls, centred, instead.
    const fit = (): void => {
      if (!hint) return;
      hint.classList.remove('raised');
      hint.classList.toggle('raised', hint.getBoundingClientRect().width < 200);
      // the law strip shares the band between the thumbs: it stands on the card while the card is there
      uiRoot.style.setProperty('--a2hs-h', `${Math.ceil(hint.getBoundingClientRect().height)}px`);
    };
    fit();
    window.addEventListener('resize', fit);
    offs.push(() => window.removeEventListener('resize', fit));
    // A finger fires on lift: no click comes for a tap made while the other thumb is on the stick.
    ok.addEventListener('pointerup', (e) => (e.pointerType === 'mouse' ? undefined : close(e)));
  };
  const hintTimer = setTimeout(showHint, 1200);
  offs.push(() => clearTimeout(hintTimer));
  offs.push(touchUi.onChange((on) => (on ? showHint() : undefined)));

  return {
    rotateCard,
    get hint() {
      return hint;
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      rotateCard.remove();
      hint?.remove();
      html.classList.remove('bh-touch', 'bh-standalone', 'bh-ios', 'bh-a2hs-open');
    },
  };
}

const HINT_KEY = 'bh.a2hsHintSeen';
/** iOS share glyph (box with an arrow up). */
const SHARE_ICON =
  '<svg class="share" viewBox="0 0 20 24" aria-hidden="true"><path d="M10 2v13M5.5 6.5 10 2l4.5 4.5M6 10H3v12h14V10h-3"/></svg>';

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}
function writeFlag(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    /* private mode / blocked storage: the hint just comes back next time */
  }
}

/** Running as a home-screen web app (iOS standalone, or an installed PWA in standalone / fullscreen). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
  const mm = typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : null;
  return !!mm && (mm('(display-mode: standalone)').matches || mm('(display-mode: fullscreen)').matches);
}

/** iPhone / iPad (iPadOS reports itself as a Mac, but with touch points). */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
}

function lockLandscape(): void {
  const o = (screen as Screen & { orientation?: ScreenOrientation & { lock?(o: string): Promise<void> } }).orientation;
  o?.lock?.('landscape').catch(() => undefined);
}

/** A short buzz where supported (Android Chrome; iOS Safari has no Vibration API). */
export function haptic(ms: number | number[]): void {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate(ms);
  } catch {
    /* some browsers throw without a recent user gesture */
  }
}

/** True while the device is held in portrait (the game asks to be turned). */
export function isPortrait(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(orientation: portrait)').matches;
}
